const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'url',
  'email',
  'password',
  'number',
  'tel',
  ''
]);

/**
 * True when keyboard input on this element edits text (so keys like Backspace must be left alone).
 * Range sliders, buttons, checkboxes etc. are *not* editable.
 */
export const isTextEditableElement = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = ((target as HTMLInputElement).getAttribute('type') || '').toLowerCase();
    return TEXT_INPUT_TYPES.has(type);
  }
  return false;
};

/** Desktop-style devices where programmatically focusing an input won't pop up a keyboard. */
export const hasFinePointer = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * Copy exactly `text` as plain text. Avoids Safari/iOS turning a URL into a rich
 * link whose display text is the current page title.
 */
export const copyPlainText = async (text: string): Promise<boolean> => {
  const value = text.trim();
  if (!value) return false;

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([value], { type: 'text/plain' }),
          'text/html': new Blob([value.replace(/&/g, '&amp;')], { type: 'text/html' }),
        }),
      ]);
      return true;
    } catch {
      // fall through
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // fall through
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.setAttribute('aria-hidden', 'true');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.width = '1px';
    textarea.style.height = '1px';
    textarea.style.opacity = '0';
    textarea.style.border = 'none';
    textarea.style.padding = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};
